import type { FileId, ExcalidrawElement } from "@excalidraw/element/types";
import type { BinaryFileData, BinaryFiles } from "@excalidraw/excalidraw/types";

import { BaseStorageBridge } from "./StorageBridge";

/**
 * HTTP Storage Bridge Implementation
 * Bridges between IndexedDB (local storage) and HTTP storage backend (collaboration)
 */
export class HttpStorageBridge extends BaseStorageBridge {
  /**
   * Sync files from IndexedDB to HTTP collaboration storage.
   * Called when library files are imported while in collaboration mode.
   */
  async syncLocalFilesToCollaboration(fileIds: FileId[]): Promise<{
    syncedFiles: FileId[];
    erroredFiles: FileId[];
  }> {
    if (!this.isInCollaborationMode() || !fileIds.length) {
      return { syncedFiles: [], erroredFiles: fileIds };
    }

    const { roomId, roomKey } = this.portal;
    if (!roomId || !roomKey) {
      console.warn("HttpStorageBridge: Missing room credentials for sync");
      return { syncedFiles: [], erroredFiles: fileIds };
    }

    try {
      // Load files from IndexedDB
      const { loadedFiles, erroredFiles: loadErrors } =
        await this.localFileManager.getFiles(fileIds);

      if (loadedFiles.length === 0) {
        return { syncedFiles: [], erroredFiles: fileIds };
      }

      // Create minimal image elements to satisfy FileManager.saveFiles interface
      const dummyElements: ExcalidrawElement[] = loadedFiles.map((file) => ({
        id: `dummy-${file.id}`,
        type: "image" as const,
        fileId: file.id,
        x: 0,
        y: 0,
        width: 100,
        height: 100,
        angle: 0 as any,
        strokeColor: "transparent",
        backgroundColor: "transparent",
        fillStyle: "solid" as const,
        strokeWidth: 0,
        strokeStyle: "solid" as const,
        roughness: 0,
        opacity: 100,
        groupIds: [],
        frameId: null,
        roundness: null,
        seed: 1,
        versionNonce: 1,
        isDeleted: false,
        boundElements: null,
        updated: Date.now(),
        link: null,
        locked: false,
        status: "saved" as const,
        scale: [1, 1] as [number, number],
        crop: null,
        version: 1,
        index: "a0" as any,
      }));

      const filesToSync = new Map<FileId, BinaryFileData>();
      loadedFiles.forEach((file) => filesToSync.set(file.id, file));

      const { savedFiles, erroredFiles: saveErrors } =
        await this.collaborationFileManager.saveFiles({
          elements: dummyElements,
          files: Object.fromEntries(filesToSync) as BinaryFiles,
        });

      const syncedFiles = Array.from(savedFiles.keys());
      const erroredFiles = [
        ...Array.from(loadErrors.keys()),
        ...Array.from(saveErrors.keys()),
      ];

      if (erroredFiles.length > 0) {
        console.warn(
          `HttpStorageBridge: Failed to sync ${erroredFiles.length} files:`,
          erroredFiles,
        );
      }

      return { syncedFiles, erroredFiles };
    } catch (error) {
      console.error(
        "HttpStorageBridge: Error during syncLocalFilesToCollaboration:",
        error,
      );
      return { syncedFiles: [], erroredFiles: fileIds };
    }
  }

  /**
   * Sync files from HTTP collaboration storage to IndexedDB for offline access.
   */
  async syncCollaborationFilesToLocal(fileIds: FileId[]): Promise<{
    syncedFiles: FileId[];
    erroredFiles: FileId[];
  }> {
    if (!fileIds.length) {
      return { syncedFiles: [], erroredFiles: [] };
    }

    try {
      const { loadedFiles, erroredFiles: loadErrors } =
        await this.collaborationFileManager.getFiles(fileIds);

      if (loadedFiles.length === 0) {
        return { syncedFiles: [], erroredFiles: fileIds };
      }

      const dummyElements: ExcalidrawElement[] = loadedFiles.map((file) => ({
        id: `dummy-${file.id}`,
        type: "image" as const,
        fileId: file.id,
        x: 0,
        y: 0,
        width: 100,
        height: 100,
        angle: 0 as any,
        strokeColor: "transparent",
        backgroundColor: "transparent",
        fillStyle: "solid" as const,
        strokeWidth: 0,
        strokeStyle: "solid" as const,
        roughness: 0,
        opacity: 100,
        groupIds: [],
        frameId: null,
        roundness: null,
        seed: 1,
        versionNonce: 1,
        isDeleted: false,
        boundElements: null,
        updated: Date.now(),
        link: null,
        locked: false,
        status: "saved" as const,
        scale: [1, 1] as [number, number],
        crop: null,
        version: 1,
        index: "a0" as any,
      }));

      const filesToSync = Object.fromEntries(
        loadedFiles.map((file) => [file.id, file]),
      ) as BinaryFiles;

      const { savedFiles, erroredFiles: saveErrors } =
        await this.localFileManager.saveFiles({
          elements: dummyElements,
          files: filesToSync,
        });

      const syncedFiles = Array.from(savedFiles.keys());
      const erroredFiles = [
        ...Array.from(loadErrors.keys()),
        ...Array.from(saveErrors.keys()),
      ];

      return { syncedFiles, erroredFiles };
    } catch (error) {
      console.error(
        "HttpStorageBridge: Error during syncCollaborationFilesToLocal:",
        error,
      );
      return { syncedFiles: [], erroredFiles: fileIds };
    }
  }

  /**
   * Ensure files are available in collaboration storage, syncing from IndexedDB if needed.
   */
  async ensureFilesInCollaboration(
    fileIds: FileId[],
  ): Promise<BinaryFileData[]> {
    if (!fileIds.length) {
      return [];
    }

    if (!this.isInCollaborationMode()) {
      const { loadedFiles } = await this.localFileManager.getFiles(fileIds);
      return loadedFiles;
    }

    try {
      const { loadedFiles, erroredFiles } =
        await this.collaborationFileManager.getFiles(fileIds);

      const missingFileIds = Array.from(erroredFiles.keys());
      if (missingFileIds.length > 0) {
        const { syncedFiles } = await this.syncLocalFilesToCollaboration(
          missingFileIds,
        );

        if (syncedFiles.length > 0) {
          const { loadedFiles: reloadedFiles } =
            await this.collaborationFileManager.getFiles(syncedFiles);
          loadedFiles.push(...reloadedFiles);
        }
      }

      return loadedFiles;
    } catch (error) {
      console.error(
        "HttpStorageBridge: Error during ensureFilesInCollaboration:",
        error,
      );

      try {
        const { loadedFiles } = await this.localFileManager.getFiles(fileIds);
        return loadedFiles;
      } catch (fallbackError) {
        console.error(
          "HttpStorageBridge: Fallback to local storage also failed:",
          fallbackError,
        );
        return [];
      }
    }
  }

  /**
   * Get files with local-first strategy (delegates to BaseStorageBridge).
   */
  async getFiles(fileIds: FileId[]): Promise<{
    loadedFiles: BinaryFileData[];
    erroredFiles: Map<FileId, true>;
  }> {
    if (!fileIds.length) {
      return { loadedFiles: [], erroredFiles: new Map() };
    }

    if (!this.isInCollaborationMode()) {
      return this.localFileManager.getFiles(fileIds);
    }

    // Delegates to BaseStorageBridge: local-first, then HTTP for missing files.
    // Does NOT trigger uploads on cache miss — that's done explicitly via
    // syncLibraryFilesToCollaboration() only.
    return super.getFiles(fileIds);
  }
}
